/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

import useOrgUnitData from '../../../state/orgUnit/UseOrgUnitData';

import validate from "./ImxDetailReportValidate";

// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "TotalScore", field: "totalScore", title: "Total Score" },
  { width: "160px", id: "SeasonName", field: "seasonname", title: "Season Name" },
  { width: "150px", id: "FirstName", field: "firstname", title: "First Name" },
  { width: "140px", id: "MiddleName", field: "middlename", title: "Middle Name" },
  { width: "150px", id: "LastName", field: "lastname", title: "Last Name" },
  { width: "130px", id: "MemberId", field: "memberid", title: "Member ID" },
  { width: "160px", id: "EventGender", field: "eventgender", title: "Competition Category" },
  { width: "95px", id: "ImxAge", field: "imxage", title: "IMX Age" },
  { width: "90px", id: "LscCode", field: "lsccode", title: "LSC Code" },
  { width: "90px", id: "ClubCode", field: "clubcode", title: "Club Code" },
  { width: "240px", id: "ClubName", field: "clubname", title: "Club Name" },
  { width: "120px", id: "NationalRank", field: "nationalrank", title: "National Rank" },
  { width: "90px", id: "ZoneRank", field: "zonerank", title: "Zone Rank" },
  { width: "90px", id: "LscRank", field: "lscrank", title: "LSC Rank" },
  { width: "90px", id: "ClubRank", field: "clubrank", title: "Club Rank" },
  { width: "120px", id: "MissingEvents", field: "missingEvents", title: "Missing Events" },
  { width: "110px", id: "NationalPercentile", field: "nationalpercentile", title: "National %ile" },
  { width: "90px", id: "ZonePercentile", field: "zonepercentile", title: "Zone %ile" },
  { width: "90px", id: "LscPercentile", field: "lscpercentile", title: "LSC %tile" },
  { width: "90px", id: "ClubPercentile", field: "clubpercentile", title: "Club %ile" }
];

const excelColumns = [
  { id: "TotalScore", field: "totalScore", title: "Total Score" },
  { id: "SeasonName", field: "seasonname", title: "Season Name" },
  { id: "FirstName", field: "firstname", title: "First Name" },
  { id: "MiddleName", field: "middlename", title: "Middle Name" },
  { id: "LastName", field: "lastname", title: "Last Name" },
  { id: "MemberId", field: "memberid", title: "Member ID" },
  { id: "EventGender", field: "eventgender", title: "Competition Category" },
  { id: "ImxAge", field: "imxage", title: "IMX Age" },
  { id: "LscCode", field: "lsccode", title: "LSC Code" },
  { id: "ClubCode", field: "clubcode", title: "Club Code" },
  { id: "ClubName", field: "clubname", title: "Club Name" },
  { id: "NationalRank", field: "nationalrank", title: "National Rank" },
  { id: "ZoneRank", field: "zonerank", title: "Zone Rank" },
  { id: "LscRank", field: "lscrank", title: "LSC Rank" },
  { id: "ClubRank", field: "clubrank", title: "Club Rank" },
  { id: "MissingEvents", field: "missingEvents", title: "Missing Events" },
  { id: "NationalPercentile", field: "nationalpercentile", title: "National Percentile" },
  { id: "ZonePercentile", field: "zonepercentile", title: "Zone Percentile" },
  { id: "LscPercentile", field: "lscpercentile", title: "LSC Percentile" },
  { id: "ClubPercentile", field: "clubpercentile", title: "Club Percentile" },
  { id: "ImxDetail_EventCode", field: "imxDetail_eventCode", title: "Event" },
  { id: "ImxDetail_SwimTime", field: "imxDetail_swimTime", title: "Time" },
  { id: "ImxDetail_SwimDate", field: "imxDetail_swimDate", title: "Date" },
  { id: "ImxDetail_MeetName", field: "imxDetail_meetName", title: "Meet" },
  { id: "ImxDetail_ImxPoints", field: "imxDetail_imxPoints", title: "Points" },
  { id: "ImxDetail_PercentTotalScore", field: "imxDetail_percentTotalScore", title: "Percent of Total Score" }
];

const REPORT_INFO_ID = 287;

const useImxDetailReport = () => {
  const [modalState, setModalState] = useState(getInitialModalState());
  const [uiState, setUiState] = useState({ isDetailRunning: false, isCertsRunning: false });
  const { orgUnitState } = useOrgUnitData(); 

  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    setUiState({ ...uiState, isDetailRunning: true, isCertsRunning: false });
    handleSubmit(e);
  }

  const onViewCertificateClicked = (e) => {
    setUiState({ ...uiState, isCertsRunning: true, isDetailRunning: false });
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      zones: [],
      clubs: [],
      lscs: [],
      genders:  [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      minAge: 10, 
      maxAge: 18, 
      imxSeason: 95, 
      missingEventsOnly: false,
      imxSeasonId: Constants.DEFAULT_ID,
      exportDetailData: false
    };
  }

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId,
      ImxSeason_Int: formState.imxSeasonId,
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      Genders: formState.genders.map(gender => gender.id).join(','), 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','),
      MissingEventsOnly_Int: formState.missingEventsOnly ? 1 : 0
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    uiState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onViewCertificateClicked,
    onClearForm,
    excelColumns: excelColumns,
    clubFilterIds: getClubFileterIds(),
    REPORT_INFO_ID: REPORT_INFO_ID
  };
};

export default useImxDetailReport;